import React, { useEffect, useRef, useState } from "react";
import { BadgeCheck, BusFront, Camera, CheckCircle2, ChevronLeft, ChevronRight, Clock3, LoaderCircle, MapPin, Radio, ShieldCheck, Upload, UserRound, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabase";
import RideRequestsPanel from "../../components/RideRequestsPanel";

const PHOTO_BUCKET = "driver-verification-photos";
const CATALOG_PHOTO_BUCKET = "vehicle-catalog-photos";
const MAX_PHOTO_SIZE = 8 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const UPLOADS = [
  { key: "vehicle", pathColumn: "vehicle_photo_path", numberField: "plate", numberColumn: "plate_number" },
  { key: "passport", pathColumn: "passport_document_path", numberField: "passportNumber", numberColumn: "passport_number" },
  { key: "license", pathColumn: "driver_license_path", numberField: "driverLicenseNumber", numberColumn: "driver_license_number" },
  { key: "registration", pathColumn: "vehicle_registration_path", numberField: "registrationNumber", numberColumn: "vehicle_registration_number" },
];

const emptyForm = (name = "") => ({
  fullName: name,
  phone: "",
  birthYear: "",
  city: "",
  experienceYears: "",
  languages: "",
  passportNumber: "",
  driverLicenseNumber: "",
  vehicle: "",
  transportType: "car",
  vehicleClass: "economy",
  passengerCapacity: "4",
  luggageCapacity: "2",
  plate: "",
  color: "",
  registrationNumber: "",
});

function PrivateUploadField({ label, hint, file, previewUrl, path, previewLabel, uploadLabel, replaceLabel, required, onChange }) {
  const isPdf = file?.type === "application/pdf" || (!file && path?.toLowerCase().endsWith(".pdf"));
  return (
    <div>
      <span className="text-sm font-bold">{label}</span>
      {hint && <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{hint}</p>}
      <label className="mt-3 flex min-h-32 cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center transition hover:border-amber-400 dark:border-slate-700 dark:bg-slate-800/60">
        {previewUrl && !isPdf ? <img src={previewUrl} alt={previewLabel} className="max-h-52 w-full rounded-xl object-contain" /> : <Camera className="h-8 w-8 text-amber-500" />}
        {isPdf && previewUrl && <a href={previewUrl} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="text-sm font-bold text-amber-700 underline dark:text-amber-300">{previewLabel}</a>}
        <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200"><Upload className="h-4 w-4 text-amber-500" />{file?.name || (path ? replaceLabel : uploadLabel)}</span>
        <input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" required={required} onChange={onChange} />
      </label>
    </div>
  );
}

function PublicVehiclePhotoField({ label, hint, file, previewUrl, path, previewLabel, uploadLabel, replaceLabel, required, onChange }) {
  return (
    <div>
      <span className="text-sm font-bold">{label}</span>
      {hint && <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{hint}</p>}
      <label className="mt-3 flex min-h-36 cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center transition hover:border-amber-400 dark:border-slate-700 dark:bg-slate-800/60">
        {previewUrl ? <img src={previewUrl} alt={previewLabel} className="max-h-56 w-full rounded-xl object-contain" /> : <Camera className="h-8 w-8 text-amber-500" />}
        <span className="inline-flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200"><Upload className="h-4 w-4 text-amber-500" />{file?.name || (path ? replaceLabel : uploadLabel)}</span>
        <input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" required={required} onChange={onChange} />
      </label>
    </div>
  );
}

const inputClass = "mt-2 min-h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500";

function DriverAvailabilityControl() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(false);
  const [locationState, setLocationState] = useState("loading");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let active = true;
    supabase?.rpc("get_my_driver_presence").then(({ data, error }) => {
      if (!active) return;
      const isOnline = !error && data?.[0]?.is_online === true;
      setOnline(isOnline);
      setLocationState(isOnline ? "waiting" : "offline");
    }).catch(() => { if (active) setLocationState("error"); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!online) return undefined;
    if (!navigator.geolocation) {
      setLocationState("unavailable");
      setOnline(false);
      return undefined;
    }
    let active = true;
    let lastUpdateAt = 0;
    let updateInFlight = false;
    setLocationState("waiting");
    const watchId = navigator.geolocation.watchPosition(async ({ coords }) => {
      const now = Date.now();
      if (!active || updateInFlight || now - lastUpdateAt < 12000) return;
      updateInFlight = true;
      try {
        const { error } = await supabase.rpc("set_driver_presence", {
          p_online: true,
          p_latitude: coords.latitude,
          p_longitude: coords.longitude,
        });
        if (error) throw error;
        lastUpdateAt = Date.now();
        if (active) {
          setLocationState("online");
          window.dispatchEvent(new CustomEvent("uztravel-driver-presence", { detail: { isOnline: true } }));
        }
      } catch (error) {
        console.error("Could not update driver availability", error);
        if (active) {
          setLocationState("error");
          setOnline(false);
          window.dispatchEvent(new CustomEvent("uztravel-driver-presence", { detail: { isOnline: false } }));
        }
      } finally {
        updateInFlight = false;
      }
    }, async (error) => {
      console.warn("Driver location permission unavailable", error);
      if (active) {
        setLocationState(error.code === 1 ? "denied" : "error");
        setOnline(false);
        window.dispatchEvent(new CustomEvent("uztravel-driver-presence", { detail: { isOnline: false } }));
      }
      await supabase.rpc("set_driver_presence", { p_online: false, p_latitude: null, p_longitude: null });
    }, { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 });
    return () => { active = false; navigator.geolocation.clearWatch(watchId); };
  }, [online]);

  const toggle = async () => {
    if (online) {
      setWorking(true);
      const { error } = await supabase.rpc("set_driver_presence", { p_online: false, p_latitude: null, p_longitude: null });
      setWorking(false);
      if (error) { setLocationState("error"); return; }
      setOnline(false);
      setLocationState("offline");
      window.dispatchEvent(new CustomEvent("uztravel-driver-presence", { detail: { isOnline: false } }));
      return;
    }
    if (!navigator.geolocation) { setLocationState("unavailable"); return; }
    setLocationState("waiting");
    setOnline(true);
  };

  const isConnected = online && locationState === "online";
  const messageKey = locationState === "online" ? "online_text" : locationState;

  return (
    <section className="mt-6 flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-5 dark:border-[#272c35] dark:bg-[#11141b] sm:flex-row sm:items-center sm:justify-between sm:p-6">
      <div className="flex items-start gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${isConnected ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300" : "bg-slate-100 text-slate-500 dark:bg-[#191d26] dark:text-slate-400"}`}><MapPin className="h-5 w-5" aria-hidden="true" /></span>
        <div><h2 className="font-extrabold text-slate-900 dark:text-white">{t("dashboard.driver_availability_title")}</h2><p className="mt-1 text-sm leading-5 text-slate-500 dark:text-slate-400">{t(`dashboard.driver_availability_${messageKey}`, t("dashboard.driver_availability_offline"))}</p></div>
      </div>
      <button type="button" onClick={toggle} disabled={working || locationState === "loading"} className={`inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl px-5 text-sm font-extrabold transition disabled:opacity-60 ${online ? "border border-red-400/30 bg-red-400/10 text-red-200 hover:bg-red-400/20" : "bg-amber-400 text-slate-950 hover:bg-amber-300"}`}>
        <Radio className="h-4 w-4" aria-hidden="true" />{working ? t("work_portal.saving") : t(online ? "dashboard.driver_go_offline" : "dashboard.driver_go_online")}
      </button>
    </section>
  );
}

export default function DriverDashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const formRef = useRef(null);
  const [form, setForm] = useState(() => emptyForm(user?.name || ""));
  const [application, setApplication] = useState(null);
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState({ vehicle: null, passport: null, license: null, registration: null, catalogVehicle: null });
  const [documentConsent, setDocumentConsent] = useState(false);
  const [catalogPhotoConsent, setCatalogPhotoConsent] = useState(false);
  const [savedPreviewUrls, setSavedPreviewUrls] = useState({});
  const [filePreviewUrls, setFilePreviewUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [loadRetry, setLoadRetry] = useState(0);

  useEffect(() => {
    let active = true;
    const loadApplication = async () => {
      setLoading(true);
      setLoadError(false);
      if (!supabase || !user?.id) {
        setLoading(false);
        setLoadError(true);
        return;
      }
      const { data, error: queryError } = await supabase
        .from("driver_verifications")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (!active) return;
      if (queryError) {
        console.error("Could not load driver verification", queryError);
        setLoadError(true);
        setLoading(false);
        return;
      }
      setApplication(data || null);
      if (data) {
        setForm({
          fullName: user.name || "",
          phone: data.phone || "",
          birthYear: data.birth_year ? String(data.birth_year) : "",
          city: data.city || "",
          experienceYears: data.experience_years === 0 || data.experience_years ? String(data.experience_years) : "",
          languages: data.languages || "",
          passportNumber: data.passport_number || "",
          driverLicenseNumber: data.driver_license_number || "",
          vehicle: data.vehicle || "",
          transportType: data.transport_type || "car",
          vehicleClass: data.vehicle_class || "economy",
          passengerCapacity: data.passenger_capacity ? String(data.passenger_capacity) : data.transport_type === "bus" ? "10" : "4",
          luggageCapacity: data.luggage_capacity === 0 || data.luggage_capacity ? String(data.luggage_capacity) : data.transport_type === "bus" ? "8" : "2",
          plate: data.plate_number || "",
          color: data.vehicle_color || "",
          registrationNumber: data.vehicle_registration_number || "",
        });
        const signedEntries = await Promise.all(UPLOADS.map(async ({ key, pathColumn }) => {
          const path = data[pathColumn];
          if (!path) return [key, ""];
          const { data: signedFile, error: signedUrlError } = await supabase.storage.from(PHOTO_BUCKET).createSignedUrl(path, 60 * 30);
          if (signedUrlError) console.warn(`Could not create private ${key} preview URL`, signedUrlError);
          return [key, signedFile?.signedUrl || ""];
        }));
        const catalogPhotoUrl = data.catalog_photo_path
          ? supabase.storage.from(CATALOG_PHOTO_BUCKET).getPublicUrl(data.catalog_photo_path).data?.publicUrl || ""
          : "";
        if (active) {
          setSavedPreviewUrls({ ...Object.fromEntries(signedEntries), catalogVehicle: catalogPhotoUrl });
          setCatalogPhotoConsent(Boolean(data.catalog_photo_consent_at));
        }
      } else {
        setSavedPreviewUrls({});
        setCatalogPhotoConsent(false);
      }
      setLoading(false);
    };
    loadApplication().catch((loadError) => {
      if (active) {
        console.error("Could not load driver verification", loadError);
        setLoadError(true);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, [user?.id, user?.name, loadRetry]);

  useEffect(() => {
    if (application?.status !== "pending") return undefined;
    const intervalId = window.setInterval(() => setLoadRetry((attempt) => attempt + 1), 12000);
    return () => window.clearInterval(intervalId);
  }, [application?.status]);

  useEffect(() => {
    const entries = Object.entries(files).filter(([, file]) => file);
    const urls = Object.fromEntries(entries.map(([key, file]) => [key, URL.createObjectURL(file)]));
    setFilePreviewUrls(urls);
    return () => Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
  }, [files]);

  const setField = (name) => (event) => setForm((current) => ({ ...current, [name]: event.target.value }));
  const setTransportType = (event) => {
    const transportType = event.target.value;
    setForm((current) => ({
      ...current,
      transportType,
      passengerCapacity: transportType === "bus" ? "10" : "4",
      luggageCapacity: transportType === "bus" ? "8" : "2",
    }));
  };

  const handleUploadChange = (key) => (event) => {
    const nextFile = event.target.files?.[0] || null;
    setError("");
    if (!nextFile) return;
    if (!ACCEPTED_FILE_TYPES.includes(nextFile.type)) {
      setError(t("dashboard.driver_verification_file_type_error"));
      event.target.value = "";
      return;
    }
    if (nextFile.size > MAX_PHOTO_SIZE) {
      setError(t("dashboard.driver_verification_file_size_error"));
      event.target.value = "";
      return;
    }
    setFiles((current) => ({ ...current, [key]: nextFile }));
  };

  const handleCatalogPhotoChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    setError("");
    if (!nextFile) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(nextFile.type)) {
      setError(t("dashboard.driver_verification_photo_type_error"));
      event.target.value = "";
      return;
    }
    if (nextFile.size > MAX_PHOTO_SIZE) {
      setError(t("dashboard.driver_verification_file_size_error"));
      event.target.value = "";
      return;
    }
    setFiles((current) => ({ ...current, catalogVehicle: nextFile }));
  };

  const continueToNextStep = () => {
    if (!formRef.current?.reportValidity()) return;
    setError("");
    setStep((current) => Math.min(current + 1, 3));
  };

  const submitApplication = async (event) => {
    event.preventDefault();
    setError("");
    if (!supabase || !user?.id) {
      setError(t("dashboard.driver_verification_setup_error_v2"));
      return;
    }
    const missingDocument = UPLOADS.some(({ key, pathColumn, numberField, numberColumn }) => {
      const currentNumber = String(form[numberField] || "").trim();
      const savedNumber = String(application?.[numberColumn] || "").trim();
      const normalizedCurrent = key === "vehicle" ? currentNumber.toUpperCase() : currentNumber;
      const normalizedSaved = key === "vehicle" ? savedNumber.toUpperCase() : savedNumber;
      return !currentNumber || (!files[key] && !application?.[pathColumn])
        || (savedNumber && normalizedCurrent !== normalizedSaved && !files[key]);
    });
    const missingCatalogPhoto = !files.catalogVehicle && !application?.catalog_photo_path;
    if (missingDocument || missingCatalogPhoto || !documentConsent || !catalogPhotoConsent) {
      const errorKey = missingDocument
        ? "dashboard.driver_verification_all_documents_required"
        : missingCatalogPhoto
          ? "dashboard.driver_verification_catalog_photo_required"
          : !documentConsent
            ? "dashboard.driver_verification_consent_required"
            : "dashboard.driver_verification_catalog_photo_consent_required";
      setError(t(errorKey));
      return;
    }

    setSaving(true);
    const uploadedPaths = [];
    let submissionStage = "private_upload";
    try {
      const paths = Object.fromEntries(UPLOADS.map(({ key, pathColumn }) => [key, application?.[pathColumn] || ""]));
      paths.catalogVehicle = application?.catalog_photo_path || "";
      for (const { key } of UPLOADS) {
        const file = files[key];
        if (!file) continue;
        const fallbackExtension = file.type === "application/pdf" ? "pdf" : file.type.split("/")[1] || "jpg";
        const extension = file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || fallbackExtension;
        const uploadedPath = `${user.id}/${crypto.randomUUID()}.${extension}`;
        const { error: uploadError } = await supabase.storage.from(PHOTO_BUCKET).upload(uploadedPath, file, {
          contentType: file.type,
          cacheControl: "3600",
          upsert: false,
        });
        if (uploadError) throw uploadError;
        uploadedPaths.push({ bucket: PHOTO_BUCKET, path: uploadedPath });
        paths[key] = uploadedPath;
      }

      if (files.catalogVehicle) {
        submissionStage = "catalog_upload";
        const file = files.catalogVehicle;
        const extension = file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || file.type.split("/")[1] || "jpg";
        const uploadedPath = `${user.id}/${crypto.randomUUID()}.${extension}`;
        const { error: uploadError } = await supabase.storage.from(CATALOG_PHOTO_BUCKET).upload(uploadedPath, file, {
          contentType: file.type,
          cacheControl: "3600",
          upsert: false,
        });
        if (uploadError) throw uploadError;
        uploadedPaths.push({ bucket: CATALOG_PHOTO_BUCKET, path: uploadedPath });
        paths.catalogVehicle = uploadedPath;
      }

      const payload = {
        user_id: user.id,
        phone: form.phone.trim(),
        birth_year: Number(form.birthYear),
        city: form.city.trim(),
        experience_years: Number(form.experienceYears),
        languages: form.languages.trim(),
        passport_number: form.passportNumber.trim(),
        passport_document_path: paths.passport,
        driver_license_number: form.driverLicenseNumber.trim(),
        driver_license_path: paths.license,
        vehicle: form.vehicle.trim(),
        transport_type: form.transportType,
        vehicle_class: form.vehicleClass,
        passenger_capacity: Number(form.passengerCapacity),
        luggage_capacity: Number(form.luggageCapacity),
        plate_number: form.plate.trim().toUpperCase(),
        vehicle_color: form.color.trim(),
        vehicle_photo_path: paths.vehicle,
        catalog_photo_path: paths.catalogVehicle,
        catalog_photo_consent_at: catalogPhotoConsent ? new Date().toISOString() : null,
        vehicle_registration_number: form.registrationNumber.trim(),
        vehicle_registration_path: paths.registration,
        identity_documents_consent_at: new Date().toISOString(),
      };
      submissionStage = "database_save";
      const { data, error: saveError } = await supabase
        .from("driver_verifications")
        .upsert(payload, { onConflict: "user_id" })
        .select("*")
        .single();
      if (saveError) throw saveError;

      const replacedPaths = UPLOADS
        .map(({ key, pathColumn }) => application?.[pathColumn] && application[pathColumn] !== paths[key] ? application[pathColumn] : null)
        .filter(Boolean);
      if (replacedPaths.length) await supabase.storage.from(PHOTO_BUCKET).remove(replacedPaths);
      if (application?.catalog_photo_path && application.catalog_photo_path !== paths.catalogVehicle) {
        await supabase.storage.from(CATALOG_PHOTO_BUCKET).remove([application.catalog_photo_path]);
      }

      const signedEntries = await Promise.all(UPLOADS.map(async ({ key, pathColumn }) => {
        if (!data[pathColumn]) return [key, ""];
        const { data: signedFile } = await supabase.storage.from(PHOTO_BUCKET).createSignedUrl(data[pathColumn], 60 * 30);
        return [key, signedFile?.signedUrl || ""];
      }));
      submissionStage = "preview_refresh";
      const catalogPhotoUrl = data.catalog_photo_path
        ? supabase.storage.from(CATALOG_PHOTO_BUCKET).getPublicUrl(data.catalog_photo_path).data?.publicUrl || ""
        : "";
      setSavedPreviewUrls({ ...Object.fromEntries(signedEntries), catalogVehicle: catalogPhotoUrl });
      setApplication(data);
      setFiles({ vehicle: null, passport: null, license: null, registration: null, catalogVehicle: null });
      setCatalogPhotoConsent(Boolean(data.catalog_photo_consent_at));
      setStep(1);
    } catch (submitError) {
      console.error("Could not submit driver verification", submitError);
      for (const bucket of new Set(uploadedPaths.map((item) => item.bucket))) {
        const pathsToRemove = uploadedPaths.filter((item) => item.bucket === bucket).map((item) => item.path);
        await supabase.storage.from(bucket).remove(pathsToRemove);
      }
      const message = String(submitError?.message || "").toLowerCase();
      const code = submitError?.code;
      const statusCode = String(submitError?.statusCode || submitError?.status || "");
      const missingSchema = ["42P01", "PGRST204", "PGRST205", "42703"].includes(code)
        || message.includes("does not exist")
        || message.includes("could not find the table")
        || message.includes("could not find the column");
      const permissionDenied = code === "42501"
        || message.includes("row-level security")
        || message.includes("permission denied");
      const missingBucket = statusCode === "404" || message.includes("bucket not found");
      const missingDocuments = message.includes("driver verification documents, consent and vehicle details are required");
      let errorKey = "dashboard.driver_verification_submit_error";
      if (missingDocuments) {
        errorKey = "dashboard.driver_verification_all_documents_required";
      } else if (missingSchema) {
        errorKey = "dashboard.driver_verification_setup_error_v2";
      } else if (missingBucket && submissionStage === "catalog_upload") {
        errorKey = "dashboard.driver_verification_catalog_setup_error";
      } else if (missingBucket) {
        errorKey = "dashboard.driver_verification_private_storage_setup_error";
      } else if (permissionDenied) {
        errorKey = "dashboard.driver_verification_access_error";
      } else if (submissionStage === "catalog_upload") {
        errorKey = "dashboard.driver_verification_catalog_upload_error";
      } else if (submissionStage === "private_upload") {
        errorKey = "dashboard.driver_verification_private_upload_error";
      } else if (submissionStage === "database_save") {
        errorKey = "dashboard.driver_verification_database_save_error";
      }
      setError(t(errorKey));
    } finally {
      setSaving(false);
    }
  };

  const title = t("dashboard.driver_requests_title");
  const status = application?.status;
  const listingReady = Boolean(application?.transport_type && application?.vehicle_class
    && application?.passenger_capacity != null && application?.luggage_capacity != null
    && application?.vehicle && application?.vehicle_color && application?.plate_number && application?.vehicle_photo_path
    && application?.catalog_photo_path && application?.catalog_photo_consent_at
    && application?.passport_number && application?.passport_document_path
    && application?.driver_license_number && application?.driver_license_path
    && application?.vehicle_registration_number && application?.vehicle_registration_path
    && application?.identity_documents_consent_at);
  const showForm = !application || status === "rejected" || (status === "approved" && !listingReady);

  if (loading) {
    return <main className="min-h-[70vh] bg-[#F7F6F3] px-4 py-14 dark:bg-slate-950"><div className="mx-auto flex max-w-xl items-center justify-center gap-3 rounded-3xl border border-slate-200 bg-white p-8 text-sm font-semibold text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300"><LoaderCircle className="h-5 w-5 animate-spin text-amber-500" />{t("dashboard.driver_verification_loading")}</div></main>;
  }

  return (
    <main className="min-h-[calc(100svh-68px)] bg-[#F7F6F3] px-4 py-8 text-slate-900 dark:bg-slate-950 dark:text-slate-100 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-400 text-slate-950"><BusFront className="h-6 w-6" /></div>
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[.18em] text-amber-600 dark:text-amber-400">{t("dashboard.driver_badge")}</p>
            <h1 className="mt-1 text-2xl font-extrabold sm:text-3xl">{showForm ? t("dashboard.driver_verification_title") : title}</h1>
          </div>
        </div>

        {loadError && <div role="alert" className="mt-6 flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200 sm:flex-row sm:items-center sm:justify-between"><p>{t(supabase ? "region_explorer.load_error" : "auth.not_configured")}</p>{supabase && <button type="button" onClick={() => setLoadRetry((attempt) => attempt + 1)} className="min-h-10 shrink-0 self-start rounded-xl border border-red-300 px-4 font-bold transition hover:bg-red-100 dark:border-red-800 dark:hover:bg-red-900/40 sm:self-auto">{t("region_explorer.retry")}</button>}</div>}

        {showForm && status === "rejected" && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
            <p className="font-extrabold">{t("dashboard.driver_verification_status_rejected")}</p>
            {application.admin_note && <p className="mt-1">{application.admin_note}</p>}
          </div>
        )}

        {showForm && status === "approved" && !listingReady && (
          <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-100">
            {t("dashboard.driver_verification_legacy_approved_update")}
          </div>
        )}

        {showForm ? (
          <section className="mt-7 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="border-b border-slate-100 p-5 dark:border-slate-800 sm:p-8">
              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">{t("dashboard.driver_verification_intro")}</p>
              <p className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-950 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-100">{t("dashboard.driver_verification_documents_intro")}</p>
              <div className="mt-5 flex items-center gap-2" aria-label={t("dashboard.driver_verification_step", { current: step, total: 3 })}>
                {[1, 2, 3].map((item) => <span key={item} className={`h-1.5 flex-1 rounded-full ${item <= step ? "bg-amber-400" : "bg-slate-200 dark:bg-slate-700"}`} />)}
              </div>
              <p className="mt-2 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{t("dashboard.driver_verification_step", { current: step, total: 3 })} · {t(["", "dashboard.driver_verification_personal_step", "dashboard.driver_verification_license_step", "dashboard.driver_verification_vehicle_step"][step])}</p>
            </div>

            <form ref={formRef} onSubmit={submitApplication} className="p-5 sm:p-8">
              {error && <p role="alert" className="mb-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">{error}</p>}

              <fieldset disabled={saving} className="min-w-0">
              {step === 1 && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_full_name")}<input className={inputClass} value={form.fullName} readOnly /></label>
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_phone")}<input className={inputClass} type="tel" autoComplete="tel" required maxLength={32} value={form.phone} onChange={setField("phone")} placeholder={t("dashboard.driver_verification_phone_placeholder")} /></label>
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_birth_year")}<input className={inputClass} type="number" required min={new Date().getFullYear() - 90} max={new Date().getFullYear() - 18} inputMode="numeric" value={form.birthYear} onChange={setField("birthYear")} placeholder="1990" /></label>
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_city")}<input className={inputClass} type="text" autoComplete="address-level2" required maxLength={80} value={form.city} onChange={setField("city")} placeholder={t("dashboard.driver_verification_city_placeholder")} /></label>
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_experience")}<input className={inputClass} type="number" required min="0" max="70" inputMode="numeric" value={form.experienceYears} onChange={setField("experienceYears")} placeholder="5" /></label>
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_languages")}<input className={inputClass} type="text" required maxLength={120} value={form.languages} onChange={setField("languages")} placeholder={t("dashboard.driver_verification_languages_placeholder")} /></label>
                  <label className="text-sm font-bold sm:col-span-2">{t("dashboard.driver_verification_passport_number")}<input className={inputClass} type="text" required maxLength={40} autoComplete="off" value={form.passportNumber} onChange={setField("passportNumber")} /></label>
                  <div className="sm:col-span-2">
                    <PrivateUploadField label={t("dashboard.driver_verification_passport_document")} hint={t("dashboard.driver_verification_document_hint")} file={files.passport} previewUrl={filePreviewUrls.passport || savedPreviewUrls.passport} path={application?.passport_document_path} previewLabel={t("dashboard.driver_verification_document_preview")} uploadLabel={t("dashboard.driver_verification_upload_document")} replaceLabel={t("dashboard.driver_verification_replace_document")} required={!files.passport && (!application?.passport_document_path || (application?.passport_number && form.passportNumber.trim() !== application.passport_number))} onChange={handleUploadChange("passport")} />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="text-sm font-bold sm:col-span-2">{t("dashboard.driver_verification_license_number")}<input className={inputClass} type="text" required maxLength={40} autoComplete="off" value={form.driverLicenseNumber} onChange={setField("driverLicenseNumber")} /></label>
                  <div className="sm:col-span-2">
                    <PrivateUploadField label={t("dashboard.driver_verification_license_document")} hint={t("dashboard.driver_verification_document_hint")} file={files.license} previewUrl={filePreviewUrls.license || savedPreviewUrls.license} path={application?.driver_license_path} previewLabel={t("dashboard.driver_verification_document_preview")} uploadLabel={t("dashboard.driver_verification_upload_document")} replaceLabel={t("dashboard.driver_verification_replace_document")} required={!files.license && (!application?.driver_license_path || (application?.driver_license_number && form.driverLicenseNumber.trim() !== application.driver_license_number))} onChange={handleUploadChange("license")} />
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_transport_type")}<select className={inputClass} value={form.transportType} onChange={setTransportType}><option value="car">{t("tickets.flow.transport_types.car.title")}</option><option value="bus">{t("tickets.flow.transport_types.bus.title")}</option></select></label>
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_vehicle_class")}<select className={inputClass} value={form.vehicleClass} onChange={setField("vehicleClass")}><option value="economy">{t("tickets.flow.classes.economy.title")}</option><option value="standard">{t("tickets.flow.classes.standard.title")}</option><option value="business">{t("tickets.flow.classes.business.title")}</option></select></label>
                  <label className="text-sm font-bold sm:col-span-2">{t("dashboard.driver_verification_vehicle")}<input className={inputClass} type="text" required maxLength={100} value={form.vehicle} onChange={setField("vehicle")} placeholder={t("dashboard.driver_verification_vehicle_placeholder")} /></label>
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_passenger_capacity")}<input className={inputClass} type="number" required min={form.transportType === "bus" ? 5 : 1} max={form.transportType === "bus" ? 50 : 4} inputMode="numeric" value={form.passengerCapacity} onChange={setField("passengerCapacity")} /></label>
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_luggage_capacity")}<input className={inputClass} type="number" required min="0" max="50" inputMode="numeric" value={form.luggageCapacity} onChange={setField("luggageCapacity")} /></label>
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_plate")}<input className={`${inputClass} uppercase`} type="text" required maxLength={20} value={form.plate} onChange={setField("plate")} placeholder="01 A 123 BC" /></label>
                  <label className="text-sm font-bold">{t("dashboard.driver_verification_color")}<input className={inputClass} type="text" required maxLength={40} value={form.color} onChange={setField("color")} placeholder={t("dashboard.driver_verification_color_placeholder")} /></label>
                  <label className="text-sm font-bold sm:col-span-2">{t("dashboard.driver_verification_registration_number")}<input className={inputClass} type="text" required maxLength={40} autoComplete="off" value={form.registrationNumber} onChange={setField("registrationNumber")} /></label>
                  <div className="sm:col-span-2">
                    <PrivateUploadField label={t("dashboard.driver_verification_photo")} hint={t("dashboard.driver_verification_photo_hint")} file={files.vehicle} previewUrl={filePreviewUrls.vehicle || savedPreviewUrls.vehicle} path={application?.vehicle_photo_path} previewLabel={t("dashboard.driver_verification_photo_preview")} uploadLabel={t("dashboard.driver_verification_upload_photo")} replaceLabel={t("dashboard.driver_verification_replace_photo")} required={!files.vehicle && (!application?.vehicle_photo_path || (application?.plate_number && form.plate.trim().toUpperCase() !== application.plate_number.trim().toUpperCase()))} onChange={handleUploadChange("vehicle")} />
                  </div>
                  <div className="sm:col-span-2">
                    <PublicVehiclePhotoField label={t("dashboard.driver_verification_catalog_photo")} hint={t("dashboard.driver_verification_catalog_photo_hint")} file={files.catalogVehicle} previewUrl={filePreviewUrls.catalogVehicle || savedPreviewUrls.catalogVehicle} path={application?.catalog_photo_path} previewLabel={t("dashboard.driver_verification_catalog_photo_preview")} uploadLabel={t("dashboard.driver_verification_catalog_photo_upload")} replaceLabel={t("dashboard.driver_verification_catalog_photo_replace")} required={!files.catalogVehicle && !application?.catalog_photo_path} onChange={handleCatalogPhotoChange} />
                  </div>
                  <div className="sm:col-span-2">
                    <PrivateUploadField label={t("dashboard.driver_verification_registration_document")} hint={t("dashboard.driver_verification_document_hint")} file={files.registration} previewUrl={filePreviewUrls.registration || savedPreviewUrls.registration} path={application?.vehicle_registration_path} previewLabel={t("dashboard.driver_verification_document_preview")} uploadLabel={t("dashboard.driver_verification_upload_document")} replaceLabel={t("dashboard.driver_verification_replace_document")} required={!files.registration && (!application?.vehicle_registration_path || (application?.vehicle_registration_number && form.registrationNumber.trim() !== application.vehicle_registration_number))} onChange={handleUploadChange("registration")} />
                  </div>
                  <label className="sm:col-span-2 flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 dark:border-slate-700 dark:bg-slate-800/60">
                    <input className="mt-1 h-4 w-4 accent-amber-400" type="checkbox" required checked={documentConsent} onChange={(event) => setDocumentConsent(event.target.checked)} />
                    <span>{t("dashboard.driver_verification_consent_text")}</span>
                  </label>
                  <label className="sm:col-span-2 flex cursor-pointer items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-100">
                    <input className="mt-1 h-4 w-4 accent-amber-400" type="checkbox" required checked={catalogPhotoConsent} onChange={(event) => setCatalogPhotoConsent(event.target.checked)} />
                    <span>{t("dashboard.driver_verification_catalog_photo_consent")}</span>
                  </label>
                </div>
              )}

              <div className="mt-7 flex flex-col-reverse gap-3 border-t border-slate-100 pt-5 dark:border-slate-800 sm:flex-row sm:justify-between">
                {step > 1 ? <button type="button" onClick={() => { setError(""); setStep((current) => current - 1); }} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 px-5 text-sm font-bold transition hover:border-amber-400 dark:border-slate-700"><ChevronLeft className="h-4 w-4" />{t("dashboard.driver_verification_back")}</button> : <span />}
                {step < 3 ? (
                  <button type="button" onClick={continueToNextStep} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-amber-400 px-6 text-sm font-extrabold text-slate-950 transition hover:bg-amber-300">{t(step === 1 ? "dashboard.driver_verification_continue_license" : "dashboard.driver_verification_continue_vehicle")}<ChevronRight className="h-4 w-4" /></button>
                ) : (
                  <button type="submit" disabled={saving || loadError} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-amber-400 px-6 text-sm font-extrabold text-slate-950 transition hover:bg-amber-300 disabled:cursor-wait disabled:opacity-60">{saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}{saving ? t("dashboard.driver_verification_sending") : t("dashboard.driver_verification_submit")}</button>
                )}
              </div>
              </fieldset>
            </form>
          </section>
        ) : (
          <section className="mt-7 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-8">
            <div className={`flex items-start gap-4 rounded-2xl border p-5 ${status === "approved" ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30" : status === "rejected" ? "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30" : "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30"}`}>
              {status === "approved" ? <BadgeCheck className="mt-0.5 h-7 w-7 shrink-0 text-emerald-600" /> : status === "rejected" ? <XCircle className="mt-0.5 h-7 w-7 shrink-0 text-red-600" /> : <Clock3 className="mt-0.5 h-7 w-7 shrink-0 text-amber-600" />}
              <div><h2 className="font-extrabold">{t(`dashboard.driver_verification_status_${status}`, t("dashboard.driver_verification_status_pending"))}</h2><p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">{t(`dashboard.driver_verification_status_${status}_text`, t("dashboard.driver_verification_status_pending_text"))}</p></div>
            </div>
            {application.admin_note && status === "rejected" && <p className="mt-4 rounded-2xl border border-red-200/70 p-4 text-sm leading-6 text-red-800 dark:border-red-900 dark:text-red-200">{application.admin_note}</p>}
            {status === "approved" && <div className="mt-6 flex flex-col gap-3 rounded-2xl bg-slate-50 p-4 text-sm dark:bg-slate-800/60 sm:flex-row sm:items-center sm:justify-between"><div className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" /><span>{listingReady ? t("dashboard.driver_vehicle_published") : t("dashboard.driver_vehicle_needs_catalog_details_v2")}</span></div>{listingReady && <Link to="/tickets" className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-xl bg-amber-400 px-4 font-extrabold text-slate-950 transition hover:bg-amber-300">{t("dashboard.driver_view_catalog")}</Link>}</div>}
            {status === "pending" && <div className="mt-6 flex items-center gap-3 rounded-2xl bg-slate-50 p-4 text-sm text-slate-600 dark:bg-slate-800/60 dark:text-slate-300"><UserRound className="h-5 w-5 text-amber-500" />{t("dashboard.driver_verification_private_notice_v2")}</div>}
            {status === "rejected" && <button type="button" onClick={() => { setError(""); setStep(1); }} className="mt-5 min-h-12 rounded-2xl bg-amber-400 px-5 text-sm font-extrabold text-slate-950">{t("dashboard.driver_verification_edit")}</button>}
          </section>
        )}

        {!showForm && status === "approved" && <><DriverAvailabilityControl /><RideRequestsPanel role="driver" className="mt-6" /></>}
      </div>
    </main>
  );
}
